import type {
	AnalyticsEvent,
	BaseItineraryPayload,
	BooleanString,
	FavoritesPayload,
	ItineraryPayload,
	PoiListPayload,
	PoiPayload,
} from '$lib/services/tracking/googleAnalytics/events';
import type {
	BuildAccommodationAvailabilityEventParams,
} from '$lib/services/tracking/googleAnalytics/type';
import { page as pageAppState } from '$app/state';
import { DistanceUnit, type GetMeStore, type ItineraryTrafficMode$options, type ValueOf } from '$houdini';
import { type TripOptionCar, tripOptionsCarName } from '$lib/components/itinerary/tripOptions';
import { poiCategoryTrackerNames } from '$lib/components/pois/constants';
import { isValueDateRange } from '$lib/components/pois/filters/utils';
import { seoIsNoIndex } from '$lib/components/seo/SEOHead.svelte';
import { track } from '$lib/services/tracking/googleAnalytics';
import {
	buildAccommodationPoiTrackingData,
	getPoiEventPayload,
} from '$lib/services/tracking/googleAnalytics/pois';
import {
	isCorridorPoiListPage,
	isDatasheetPage,
	isFavoritesPage,
	isHomePage,
	isRouteDetailsPageContext,
	isRoutePoiListPage,
	isRouteResultsPage,
	isZoiPoiListPage,
} from '$lib/services/urls/routeIds';
import { waitForStoreCondition } from '$lib/utils/store';
import { get, type Readable } from 'svelte/store';

type PageState = typeof pageAppState;

function getStoreValue<T>(store: Readable<T> | undefined): T | undefined {
	return store ? get(store) : undefined;
}

export async function trackPageView(pageState: PageState) {
	const { seoDataStore } = pageState.data;
	const { status } = pageState;

	const [
		itineraryPayload,
		mapResultPayload,
		poiListPayload,
		favoritesPayload,
	] = await Promise.all([
		buildItineraryPayload(pageState),
		buildPoiListPayload(pageState),
		buildPoiDatasheetPayload(pageState),
		buildFavoritesPayload(pageState),
	]);
	const seoData = getStoreValue(seoDataStore);

	track({
		action: 'page_view',
		payload: {
			full_url: window.location.href,
			http_status_code: String(status),
			noindex: booleanToString(seoIsNoIndex(seoData)),
			canonical_path_result: seoData?.arrivalHierarchyPath || undefined,
			...itineraryPayload,
			...mapResultPayload,
			...poiListPayload,
			...favoritesPayload,
		},
	});
}

export function booleanToString(value: boolean): BooleanString {
	return value ? 'true' : 'false';
}

const trafficModeMapBool: Record<ItineraryTrafficMode$options, boolean> = {
	ALL: true,
	CLOSINGS: false,
	NONE: false,
};

export function trafficModeIsWithTraffic(trafficMode: ItineraryTrafficMode$options | undefined): boolean {
	if (!trafficMode) return false;
	return trafficModeMapBool[trafficMode] ?? false;
}

async function buildPoiDatasheetPayload(pageState: PageState): Promise<PoiPayload | undefined> {
	const { route } = pageState;
	if (isDatasheetPage(route)) {
		const { poisStore, getPOIStore } = pageState.data;
		if (!poisStore || !getPOIStore) {
			return;
		}

		return waitForStoreCondition(
			[poisStore, getPOIStore],
			([
				poisStoreValue,
				getPOIStoreValue,
			]) => {
				return (
					!poisStoreValue.fetching
					&& !!poisStoreValue.selectedPoiData
					&& getPOIStoreValue
					&& !getPOIStoreValue.fetching
					&& !!(getPOIStoreValue.data?.getPOIFromId)
				);
			},
			([
				poisStoreValue,
				getPOIStoreValue,
			]) => {
				const getPOIFromId = getPOIStoreValue.data?.getPOIFromId;
				if (!poisStoreValue.selectedPoiData || !getPOIFromId) {
					return;
				}

				const { cityHierarchy } = getPOIFromId;

				return {
					...getPoiEventPayload(poisStoreValue.selectedPoiData),
					canonical_path_result: cityHierarchy
						? cityHierarchy.map(({ value }) => value).join('/')
						: undefined,
				};
			},
		);
	}
}

async function buildItineraryPayload(pageState: PageState): Promise<ItineraryPayload | undefined> {
	const { route } = pageState;
	const isRouteResultsOrDetailsPageContext = isRouteResultsPage(route) || isRouteDetailsPageContext(route);
	if (!isRouteResultsOrDetailsPageContext) {
		return;
	}

	const { itineraryResultsStore } = pageState.data;
	if (!itineraryResultsStore) {
		return;
	}

	return waitForStoreCondition(
		[itineraryResultsStore],
		([itineraryResults]) => !itineraryResults.fetching && itineraryResults.isSuccessResult,
		() => buildItineraryTrackingData(pageState),
	);
}

async function buildPoiListPayload(pageState: PageState): Promise<PoiListPayload | undefined> {
	const { poisStore } = pageState.data;
	const { route, url } = pageState;
	const isHomePageWithPoiCategory = isHomePage(url) && Boolean(pageState.url.searchParams.get('poiCategories'));
	const shouldShowPoiListPayload = isZoiPoiListPage(route) || isCorridorPoiListPage(route) || isRoutePoiListPage(route) || isHomePageWithPoiCategory;

	if (!poisStore || !shouldShowPoiListPayload) {
		return undefined;
	}

	return waitForStoreCondition(
		[poisStore],
		([poisStoreContent]) => !poisStoreContent.fetching,
		([poisStoreContent]) => {
			const totalAccommodations = poisStoreContent.availabilityStatus?.totalAccommodationsCount;
			const availableAccommodations = poisStoreContent.availabilityStatus?.availableAccommodationsCount;
			const fullyBookedAccommodations = typeof totalAccommodations === 'number' && typeof availableAccommodations === 'number'
				? totalAccommodations - availableAccommodations
				: undefined;

			return {
				nb_fully_booked_accommodation: fullyBookedAccommodations,
				nb_poi_accommodation: totalAccommodations ?? undefined,
			};
		},
	);
}

async function buildFavoritesPayload(pageState: PageState): Promise<FavoritesPayload | undefined> {
	if (!isFavoritesPage(pageState.route)) {
		return undefined;
	}
	const { userProfileStore } = pageState.data;

	if (!userProfileStore) {
		return undefined;
	}

	return waitForStoreCondition<[GetMeStore]>(
		[userProfileStore],
		([userContent]) => !userContent.fetching && !!userContent.data?.me?.itinerariesFavorites,
		([userContent]) => {
			return {
				nb_iti_favorites: userContent.data?.me?.itinerariesFavorites?.length?.toString(),
			};
		},
	);
}

export function filterDefaultCarId(car: TripOptionCar | undefined) {
	if (!car || car.isDefault) {
		return undefined;
	}
	return car.id;
}

function getDistanceForTracking(
	distance:
		| {
			value: number;
			unit: ValueOf<typeof DistanceUnit>;
		}
		| undefined,
) {
	if (!distance) {
		return undefined;
	}

	return distance.unit !== DistanceUnit.METER
		? distance.value.toString()
		: (distance.value / 1000).toString();
}

function getSelectedRouteFromPage(pageState: PageState) {
	const { searchParams, itineraryResultsStore } = pageState.data;
	const itineraryResults = getStoreValue(itineraryResultsStore);
	const selectedRouteIndex = searchParams.selectedRoute ?? 0;

	return {
		selectedRoute: itineraryResults?.routes?.[selectedRouteIndex] ?? undefined,
		selectedRouteIndex,
	};
}

export function buildBaseItineraryTrackingData(pageState: PageState): BaseItineraryPayload {
	const { searchParams } = pageState.data;
	const car = searchParams.car;
	const { selectedRoute } = getSelectedRouteFromPage(pageState);
	const { departureAddress, arrivalAddress } = getDepartureAndArrivalAddresses(pageState);

	return {
		car_id: filterDefaultCarId(car),
		car_name: car
			? tripOptionsCarName.serialize(car)
			: undefined,
		travel_mode: searchParams.travelMode,
		unit: searchParams.distanceSystem === 'IMPERIAL' ? 'mi' : 'm',
		price_kwh: selectedRoute?.isEv ? searchParams?.energyPrice?.toString() : undefined,
		range_rating_alert: selectedRoute?.isEv ? searchParams?.rechargeTreshold?.toString() : undefined,
		route_preference: searchParams.tripConstraint,
		drive_with_electric_vehicle: booleanToString(!!selectedRoute?.isEv),
		drive_with_caravan: booleanToString(!!searchParams.withCaravan),
		drive_with_traffic: booleanToString(trafficModeIsWithTraffic(searchParams?.traffic)),

		// Departure
		zip_departure: departureAddress?.zipCode || undefined,
		city_departure: departureAddress?.city || undefined,

		// Arrival
		city_arrival: arrivalAddress?.city || undefined,
		zip_arrival: arrivalAddress?.zipCode || undefined,
	};
}

function getDepartureAndArrivalAddresses(pageState: PageState) {
	const { itineraryResultsStore } = pageState.data;
	const itineraryResults = getStoreValue(itineraryResultsStore);

	return {
		departureAddress: itineraryResults?.departureAddress ?? undefined,
		arrivalAddress: itineraryResults?.arrivalAddress ?? undefined,
	};
}

export function buildItineraryTrackingData(pageState: PageState): ItineraryPayload {
	const { itineraryResultsStore } = pageState.data;
	const itineraryResults = getStoreValue(itineraryResultsStore);

	const { departureAddress, arrivalAddress } = getDepartureAndArrivalAddresses(pageState);
	const { selectedRoute, selectedRouteIndex } = getSelectedRouteFromPage(pageState);

	const waypointDeparture = itineraryResults?.waypoints?.[0];

	const departureData = {
		country_departure_name: departureAddress?.country?.name || undefined,
		country_departure: departureAddress?.country?.code || undefined,
		region_departure: departureAddress?.region || undefined,
		dpt_departure: departureAddress?.department || undefined,
		zip_departure: departureAddress?.zipCode || undefined,
		city_departure: departureAddress?.city || undefined,
		canonical_path_departure: itineraryResults?.departureCityPath || undefined,
		latitude_departure: waypointDeparture ? waypointDeparture.location.lat.toString() : undefined,
		longitude_departure: waypointDeparture ? waypointDeparture.location.lng.toString() : undefined,
	};

	const waypointArrival = itineraryResults?.waypoints?.at(-1);

	const arrivalData = {
		country_arrival_name: arrivalAddress?.country?.name || undefined,
		country_arrival: arrivalAddress?.country?.code || undefined,
		region_arrival: arrivalAddress?.region || undefined,
		dpt_arrival: arrivalAddress?.department || undefined,
		zip_arrival: arrivalAddress?.zipCode || undefined,
		city_arrival: arrivalAddress?.city || undefined,
		canonical_path_arrival: itineraryResults?.arrivalCityPath || undefined,
		latitude_arrival: waypointArrival ? waypointArrival.location.lat.toString() : undefined,
		longitude_arrival: waypointArrival ? waypointArrival.location.lng.toString() : undefined,
	};

	return {
		...buildBaseItineraryTrackingData(pageState),
		cost: selectedRoute?.totalCost?.amount?.toString(),
		distance: getDistanceForTracking(selectedRoute?.routeDistance),
		itinerary_type: selectedRoute?.types[0],
		itinerary_exploration: typeof selectedRouteIndex === 'number' ? String(selectedRouteIndex) : '',
		toll_cost: selectedRoute?.costs?.tolls?.amount?.toString(),
		user_currency: selectedRoute?.totalCost.currency,
		...departureData,
		...arrivalData,
	};
}

export function trackItinerary(
	name: 'itinerary_details' | 'itinerary_edit' | 'itinerary_selector' | 'itinerary_summary',
) {
	track({
		action: name,
		payload: {
			action: name === 'itinerary_details' || 'itinerary_selector' || 'itinerary_summary' ? 'selection' : 'edit',
			...buildItineraryTrackingData(pageAppState),
		},
	});
}

export function trackItineraryAction(name: 'costs_iti_details' | 'roadsheet_iti_details' | 'ontheway_iti_details' | 'exploration_iti_details' | 'composant_zoi') {
	track({
		action: 'call_to_action',
		payload: {
			cta_type: name,
			...buildItineraryTrackingData(pageAppState),
		},
	});
}

export function getAccommodationAvailabilityEvent({
	poi,
	searchParams,
	offer,
	context,
}: BuildAccommodationAvailabilityEventParams): AnalyticsEvent {
	const searchParamsFilters = searchParams.filters;
	const dateRange = searchParamsFilters?.ACCOMMODATION_AVAILABILITY_DATE_RANGE;
	let checkin;
	let checkout;
	if (isValueDateRange(dateRange)) {
		checkin = dateRange?.startDate;
		checkout = dateRange?.endDate;
	}
	return {
		action: 'call_to_action',
		payload: {
			cta_type: 'datasheet_availability',
			checkin_date: checkin,
			checkout_date: checkout,
			poi_category: poiCategoryTrackerNames.ACCOMMODATION,
			offer,
			context,
			...buildAccommodationPoiTrackingData(poi),
		},
	};
}

export function trackLogoClick() {
	track({
		action: 'logo_back_to_homepage',
		payload: {
			action: 'click',
		},
	});
}
